<template>
    <div
        class="question-row question-row-zipcode"
        :data-storelocator="props.content.connect_store_locator?.value"
        :data-no-branch="props.content.no_branch_dialog?.value"
        :data-question-to="props.content.answer.next_question?.value"
        :data-lead-service-name="name"
    >
        <transition name="floating-label">
            <p
                v-if="hasFloatingLabel && fieldValue !== ''"
                class="input__floating-label"
            >
                {{ props.content.answer.placeholder?.value || formTranslation('zipcode_1') }}
            </p>
        </transition>

        <input
            :id="props.content.name"
            ref="inputElement"
            :value="fieldValue"
            form="responseForm"
            class="question-answer"
            data-input-type="ExtraData"
            :name="props.content.name"
            :placeholder="props.content.answer.placeholder.value || formTranslation('zipcode_1')"
            :data-mask="props.content.answer.mask.value"
            :data-validation="props.content.answer.validation.value"
            autocapitalize="characters"
            :class="(hasFloatingLabel && fieldValue !== '') && 'input--floating-label'"
            @input="set"
        >
    </div>

    <div class="question-row question-row-button">
        <button
            class="question-button-next button"
            type="button"
            value="next"
            data-showstopper="none"
            data-question-to="next_question"
            @click="check"
        >
            <zoe-atom-text
                class="label"
                :item="props.content.button_label.value"
            />
            <svg
                class="loader loader--quiz"
                width="120"
                height="30"
                viewBox="0 0 120 30"
                xmlns="http://www.w3.org/2000/svg"
                fill="#fff"
            >
                <circle
                    cx="15"
                    cy="15"
                    r="15"
                >
                    <animate
                        attributeName="r"
                        from="15"
                        to="15"
                        begin="0s"
                        dur="0.8s"
                        values="15;9;15"
                        calcMode="linear"
                        repeatCount="indefinite"
                    />
                    <animate
                        attributeName="fill-opacity"
                        from="1"
                        to="1"
                        begin="0s"
                        dur="0.8s"
                        values="1;.5;1"
                        calcMode="linear"
                        repeatCount="indefinite"
                    />
                </circle>
                <circle
                    cx="60"
                    cy="15"
                    r="9"
                    fill-opacity="0.3"
                >
                    <animate
                        attributeName="r"
                        from="9"
                        to="9"
                        begin="0s"
                        dur="0.8s"
                        values="9;15;9"
                        calcMode="linear"
                        repeatCount="indefinite"
                    />
                    <animate
                        attributeName="fill-opacity"
                        from="0.5"
                        to="0.5"
                        begin="0s"
                        dur="0.8s"
                        values=".5;1;.5"
                        calcMode="linear"
                        repeatCount="indefinite"
                    />
                </circle>
                <circle
                    cx="105"
                    cy="15"
                    r="15"
                >
                    <animate
                        attributeName="r"
                        from="15"
                        to="15"
                        begin="0s"
                        dur="0.8s"
                        values="15;9;15"
                        calcMode="linear"
                        repeatCount="indefinite"
                    />
                    <animate
                        attributeName="fill-opacity"
                        from="1"
                        to="1"
                        begin="0s"
                        dur="0.8s"
                        values="1;.5;1"
                        calcMode="linear"
                        repeatCount="indefinite"
                    />
                </circle>
            </svg>
        </button>
    </div>
</template>

<script setup>
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { addExtraData, getQuestionName, questionProps, storeExtraData } from '@mixins/quiz/v2.0.0/question';

import ZoeAtomText from '../../../../atoms/text/v1.0.0/zoe-atom-text.vue';
import { checkCompleted } from '@mixins/quiz/v2.0.0/checks';
import { getNextQuestion } from '@mixins/quiz/v2.0.0/get-next-question';
import { removeInvalid, setInvalid } from '@mixins/quiz/v2.0.0/invalid';
import { validate } from '../../../../../js/vue-flows/default-components/sb-validate/v2/validate';
import { useMask } from '../../../../../js/vue-flows/default-components/sb-mask/v2/mask';
import { disableButton } from '@mixins/quiz/v2.0.0/buttons';
import { setStoreLocator, storeLocatorCheck } from '@organisms/store-locator/store-locator';
import { formTranslation } from '../../../../../../../resources/frontend/js/mixins/translations';
import { zipcodeIsAllowed } from '../../../../../../../resources/frontend/js/components/zipcode-lists';
import { prefillAddressBE, prefillAddressFR } from '@mixins/form/prefill-address';
import stripHtml from '../../../../../mixins/utils/strip-html';
import getCurrentStep from '@mixins/utils/get-current-step';

const store = useStore();
const props = defineProps(questionProps);

const name = getQuestionName(props);
const uuid = ref(props.content?._info?.uuid);

const inputElement = ref(null);

const fieldValue = computed(() => {
    return store.state.quiz.extradata[name]?.value || '';
});

const currentStep = computed(() => {
    return getCurrentStep();
});

const hasFloatingLabel = computed(() => {
    return store.getters['styling/getInputForPlacement'](currentStep.value.key, 'floatingLabel');
});

const set = event => {
    const maskedValue = useMask('quiz', event.target, name);
    event.target.value = maskedValue;

    storeExtraData(name, 'value', maskedValue);
    storeExtraData(name, 'label', maskedValue);
};

onBeforeMount(() => {
    addExtraData(name, {
        name,
        value: '',
        mask: 'zipcode',
        validator: props.content.answer.validation.value || 'zipcode',
        uuid: uuid.value,
        question: stripHtml(props.content.text.value),
        sendData: props.content.send_to_lead_service?.value !== false,
    });
    setStoreLocator();
});

onMounted(() => {
    useMask('quiz', inputElement.value, name);
    addZipcodeField();
});

const check = event => {
    const questionWrapper = event.target.closest('.question');
    const input = questionWrapper.querySelector('input, select');
    const inputContainer = questionWrapper.querySelector('.question-row');
    const buttons = questionWrapper.querySelectorAll('.button');
    const delay = questionWrapper.dataset.delay ? parseInt(questionWrapper.dataset.delay) : 0;

    storeExtraData(name, 'value', input.value);
    storeExtraData(name, 'label', input.value);
    setZipcodeField(input.value);
    store.commit('user/setLocationValue', { key: 'zipcode', value: input.value });

    if (!validate('quiz', name, input.value)) {
        setInvalid(inputContainer);
        return;
    }

    const answerValue = input.value;
    const listId = props.content.answer.list_id?.value;

    if (store.state.page.zipcodeLists && !zipcodeIsAllowed(answerValue, listId)) {
        storeExtraData('value', '');
        setZipcodeField('');
        inputContainer.classList.add('invalid');
        removeInvalid(inputContainer);
        return;
    }

    // temporary for test
    if (store.state.page.customer === 'Miracle-Ear Louisiana' || store.state.page.customer === 'John Doe' || store.state.page.customer === 'BirdDog Media LLC') {
        const countryCode = store.state.page.countryCode;
        const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
        const country = regionNames.of(countryCode.toUpperCase());

        fetch(`/api/zipcode-info?zipcode=${answerValue}&country=${country}&countryCode=${countryCode}&lang=${store.state.page.language}`)
            .then(response => response.json())
            .then(data => {
                store.commit('user/setLocationValue', {
                    key: 'latitude',
                    value: data.lat || '',
                });
                store.commit('user/setLocationValue', {
                    key: 'longitude',
                    value: data.lon || '',
                });
            });
    }

    if (store.state.page.countryCode === 'be') {
        prefillAddressBE();
    }

    if (store.state.page.countryCode === 'fr') {
        prefillAddressFR();
    }

    for (const question of store.state.quiz.questionsData) {
        if (question._info.type === 'zipcode' && question.connect_store_locator?.value) {
            const button = buttons[0];

            // set data to be accessible by callback functions.
            store.commit('quiz/setZipcode', { key: 'previousValue', value: answerValue });
            store.commit('quiz/setZipcode', { key: 'inputContainer', value: inputContainer });
            store.commit('quiz/setZipcode', { key: 'button', value: button });
            store.commit('quiz/setZipcode', { key: 'noBranchDialog', value: question.no_branch_dialog?.value });
            store.commit('quiz/setZipcode', {
                key: 'preventCloseDialog',
                value: question.no_branch_dialog?.hide_showstopper_close_button || false,
            });
            store.commit('quiz/setZipcode', { key: 'nextQuestion', value: inputContainer.dataset.questionTo });

            store.state.storeLocator.runningQuiz = true;
            storeLocatorCheck();
            store.state.storeLocator.usedZipcode = answerValue;

            button.classList.add('active');
            disableButton(button);

            return;
        }
    }

    store.commit('form/setPrefillZipcode', answerValue);
    const linkedFormInputIdentifier = '';
    const worktypeId = '';
    const enquiryDashboardRedirectVersion = '';
    const nextQuestion = getNextQuestion(inputContainer.dataset.questionTo, store.state.quiz.currentQuestion);

    checkCompleted(name, buttons, props.preHook, nextQuestion, props.afterHook, answerValue, linkedFormInputIdentifier, worktypeId, enquiryDashboardRedirectVersion, delay);
};

const addZipcodeField = () => {
    store.commit('form/addField', {
        step: 0,
        name: 'zipcode',
        data: {
            name: 'zipcode',
            validator: 'zipcode',
            value: '',
        },
    });
};

const setZipcodeField = value => {
    store.commit('form/setFieldData', {
        name: 'zipcode',
        key: 'value',
        value,
    });
};
</script>

<style lang="scss">
@use 'zoe-quiz-zipcode';
</style>
